/**
 * CredentialList Component
 *
 * Table component for displaying credentials with:
 * - Token masking (first/last 4 characters visible)
 * - Copy-to-clipboard functionality
 * - Expiry warnings (yellow < 7 days, red = expired)
 * - Edit/Delete actions
 *
 * @module components/admin/CredentialList
 */

import { useState, useCallback, memo } from 'react';
import {
  Key,
  Loader2,
  Edit3,
  Trash2,
  Copy,
  Check,
  Clock,
  AlertTriangle,
  AlertCircle,
  GitBranch,
  Globe,
  Settings,
  MoreVertical
} from 'lucide-react';

// ============================================
// Constants
// ============================================

const CREDENTIAL_TYPES = {
  REPOSITORY: 'repository',
  API: 'api',
  CUSTOM: 'custom'
};

const TYPE_CONFIG = {
  [CREDENTIAL_TYPES.REPOSITORY]: { icon: GitBranch, label: 'Repository', color: 'text-green-400 bg-green-500/10' },
  [CREDENTIAL_TYPES.API]: { icon: Globe, label: 'API', color: 'text-blue-400 bg-blue-500/10' },
  [CREDENTIAL_TYPES.CUSTOM]: { icon: Settings, label: 'Custom', color: 'text-purple-400 bg-purple-500/10' }
};

// ============================================
// Utility Functions
// ============================================

/**
 * Static masked placeholder for credential values
 * Backend never exposes actual values via REST API - only via XML tag for agents
 */
const MASKED_VALUE = '••••••••••••';

/**
 * Format date for display
 */
function formatDate(dateString) {
  if (!dateString) return null;
  const date = new Date(dateString);
  return date.toLocaleDateString('de-DE', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
}

/**
 * Calculate expiry status
 */
function getExpiryStatus(expiresAt) {
  if (!expiresAt) return { status: 'none', label: 'No expiry' };

  const now = new Date();
  const expiry = new Date(expiresAt);
  const diffDays = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));

  if (diffDays < 0) {
    return { status: 'expired', label: 'Expired', color: 'text-red-400 bg-red-500/10' };
  }
  if (diffDays <= 7) {
    return { status: 'warning', label: `${diffDays}d left`, color: 'text-yellow-400 bg-yellow-500/10' };
  }
  return { status: 'ok', label: formatDate(expiresAt), color: 'text-gray-400' };
}

/**
 * Format relative time
 */
function formatRelativeTime(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
  return formatDate(dateString);
}

// ============================================
// Copy Button Component
// ============================================

const CopyButton = memo(function CopyButton({ value, label = 'Copy' }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  }, [value]);

  return (
    <button
      onClick={handleCopy}
      className="p-1.5 text-gray-500 hover:text-gray-300 transition-colors rounded"
      title={copied ? 'Copied!' : label}
    >
      {copied ? (
        <Check className="w-3.5 h-3.5 text-green-400" />
      ) : (
        <Copy className="w-3.5 h-3.5" />
      )}
    </button>
  );
});

// ============================================
// Type Badge Component
// ============================================

const TypeBadge = memo(function TypeBadge({ type }) {
  const config = TYPE_CONFIG[type] || TYPE_CONFIG[CREDENTIAL_TYPES.CUSTOM];
  const Icon = config.icon;

  return (
    <span className={`
      inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium
      ${config.color}
    `}>
      <Icon className="w-3 h-3" />
      <span>{config.label}</span>
    </span>
  );
});

// ============================================
// Expiry Badge Component
// ============================================

const ExpiryBadge = memo(function ExpiryBadge({ expiresAt }) {
  const { status, label, color } = getExpiryStatus(expiresAt);

  if (status === 'none') {
    return <span className="text-xs text-gray-500">—</span>;
  }

  const Icon = status === 'expired' ? AlertCircle : status === 'warning' ? AlertTriangle : Clock;

  return (
    <span className={`
      inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium
      ${color}
    `}>
      <Icon className="w-3 h-3" />
      <span>{label}</span>
    </span>
  );
});

// ============================================
// Credential Row Component
// ============================================

const CredentialRow = memo(function CredentialRow({
  credential,
  onEdit,
  onDelete
}) {
  const [showActions, setShowActions] = useState(false);

  return (
    <tr
      className="border-b border-gray-800 hover:bg-gray-800/50 transition-colors"
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
    >
      {/* Name & Description */}
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <Key className="w-4 h-4 text-gray-500" />
          <div>
            <div className="text-sm font-medium text-gray-200">{credential.name}</div>
            {credential.description && (
              <div className="text-xs text-gray-500 truncate max-w-[200px]">
                {credential.description}
              </div>
            )}
          </div>
        </div>
      </td>

      {/* Type */}
      <td className="px-4 py-3">
        <TypeBadge type={credential.type} />
      </td>

      {/* Value (always masked - backend never exposes) */}
      <td className="px-4 py-3">
        <div className="flex items-center gap-1">
          <code className="text-xs text-gray-400 font-mono bg-gray-800 px-2 py-1 rounded">
            {MASKED_VALUE}
          </code>
          <CopyButton value={credential.name} label="Copy name" />
        </div>
      </td>

      {/* Expiry */}
      <td className="px-4 py-3">
        <ExpiryBadge expiresAt={credential.expiresAt} />
      </td>

      {/* Updated */}
      <td className="px-4 py-3 text-xs text-gray-500">
        {formatRelativeTime(credential.updatedAt)}
      </td>

      {/* Actions */}
      <td className="px-4 py-3">
        <div className={`flex items-center gap-1 transition-opacity ${showActions ? 'opacity-100' : 'opacity-0'}`}>
          <button
            onClick={() => onEdit(credential)}
            className="p-1.5 text-gray-500 hover:text-indigo-400 transition-colors rounded
                       hover:bg-indigo-500/10"
            title="Edit"
          >
            <Edit3 className="w-4 h-4" />
          </button>
          <button
            onClick={() => onDelete(credential)}
            className="p-1.5 text-gray-500 hover:text-red-400 transition-colors rounded
                       hover:bg-red-500/10"
            title="Delete"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </td>
    </tr>
  );
});

// ============================================
// Empty State Component
// ============================================

const EmptyState = memo(function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-gray-500">
      <Key className="w-12 h-12 mb-4 opacity-30" />
      <p className="text-lg font-medium mb-1">No credentials found</p>
      <p className="text-sm">Create a new credential to get started.</p>
    </div>
  );
});

// ============================================
// Loading State Component
// ============================================

const LoadingState = memo(function LoadingState() {
  return (
    <div className="flex items-center justify-center py-12 text-gray-500">
      <Loader2 className="w-6 h-6 animate-spin mr-2" />
      <span>Loading credentials...</span>
    </div>
  );
});

// ============================================
// Main CredentialList Component
// ============================================

export function CredentialList({
  credentials,
  isLoading,
  onEdit,
  onDelete
}) {
  if (isLoading) {
    return <LoadingState />;
  }

  if (!credentials || credentials.length === 0) {
    return <EmptyState />;
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-gray-700">
      <table className="w-full">
        <thead>
          <tr className="bg-gray-800/50 border-b border-gray-700">
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
              Name
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
              Type
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
              Value
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
              Expires
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
              Updated
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider w-20">
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {credentials.map((credential) => (
            <CredentialRow
              key={credential.id}
              credential={credential}
              onEdit={onEdit}
              onDelete={onDelete}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default CredentialList;
